import { useLabelVersion } from '../../../features/labels/LabelsProvider';
import { labelId, labelRegion, labelArea, labelCentro } from '../../../features/labels/labels';
import React, { useState, useMemo, useEffect } from 'react'
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  Divider
} from '@mui/material'
import { useServer } from '../../../context/ServerContext';
import CloseIcon from '@mui/icons-material/Close'
import './utils/CentersGrid.css'

import { compare_analytic_centers,compare_analytic_areas } from '../utils/api'
import {
  findScappSelectionLabel,
  getScappCompareKey,
  isSameScappSelection,
} from '../utils/compareSelection';


// --- Helpers de normalización -----------------------------

// Corrige claves mal escritas dentro de "value"
const normalizeValueKeys = (value = {}) => {
  const out = { ...value };

  if (Object.prototype.hasOwnProperty.call(out, "visitas_cout")) {
    out.visitas_count = out.visitas_cout;
    delete out.visitas_cout;
  }
  if (Object.prototype.hasOwnProperty.call(out, "perdidos_cout")) {
    out.perdidos_count = out.perdidos_cout;
    delete out.perdidos_cout;
  }

  return out;
};

// Normaliza un values_summary que puede venir como ARRAY de entries
// o como OBJETO indexado por día.
const normalizeValuesSummary = (values_summary) => {
  if (!values_summary) return values_summary;

  // Caso array: [{ day, value }, ...]
  if (Array.isArray(values_summary)) {
    return values_summary.map(entry => ({
      ...entry,
      value: normalizeValueKeys({ ...(entry?.value ?? {}) })
    }));
  }

  // Caso objeto: { "dd/mm/yyyy": { ...value }, ... }
  if (typeof values_summary === "object") {
    return Object.fromEntries(
      Object.entries(values_summary).map(([day, value]) => [
        day,
        normalizeValueKeys({ ...(value ?? {}) })
      ])
    );
  }

  return values_summary;
};

// Normaliza una "jaula" con su values_summary
const normalizeJaula = (jaula = {}) => ({
  ...jaula,
  values_summary: normalizeValuesSummary(jaula.values_summary)
});

// Normaliza un analytic_data { idJaula: { name, values_summary }, ... }
const normalizeAnalyticData = (analytic_data) => {
  if (!analytic_data || typeof analytic_data !== "object") return analytic_data;
  return Object.fromEntries(
    Object.entries(analytic_data).map(([key, jaula]) => [key, normalizeJaula(jaula)])
  );
};

// Normaliza el resultado completo que puede ser ARRAY o un OBJETO
const normalizeVersusResult = (res) => {
  if (Array.isArray(res)) {
    return res.map(item => ({
      ...item,
      analytic_data: normalizeAnalyticData(item?.analytic_data)
    }));
  }
  if (res && typeof res === "object") {
    return {
      ...res,
      analytic_data: normalizeAnalyticData(res.analytic_data)
    };
  }
  return res;
};


const SelectorData = ({
  translate,
  permisosCentros,
  permisosJaula,
  nivelSeleccionado,
  isOpen,
  closeDrawer,
  selectedCenters,
  setSelectedCenters,
  dias,
  versus,
  setVersus
}) => {
  useLabelVersion();
  const { servidor } = useServer();
  const t = (key, fallback, params) => {
    const value = typeof translate === 'function' ? translate(key, params) : key;
    return value && value !== key ? value : fallback;
  };
  const maxVersus = 4
  
  const [inputValue, setInputValue] = useState('')

  // Asegurarnos de que selectedCenters sea siempre un array
  const safeSelectedCenters = Array.isArray(selectedCenters) ? selectedCenters : []
  const selectedKeys = useMemo(
    () => safeSelectedCenters.map(item => getScappCompareKey(item, nivelSeleccionado)),
    [safeSelectedCenters, nivelSeleccionado]
  );

  // Límite de selecciones alcanzado?
  const isMaxReached = safeSelectedCenters.length >= maxVersus

  // --- Para permisosCentros: agrupar por región y área, almacenándolo en un objeto ---
  const regions = useMemo(() => {
    if (!permisosCentros || !permisosCentros.centers) return {}
    const map = {}
    permisosCentros.centers.forEach(entry => {
      // entry = "region-area-center"
      const [region, area, center] = entry.split('-')
      if (!region || !area || !center) return

      if (!map[region]) map[region] = {}
      if (!map[region][area]) map[region][area] = []
      map[region][area].push(center)
    })
    return map
  }, [permisosCentros])


  const regionAreaMap = useMemo(() => {
    if (!permisosCentros || !permisosCentros.centers) return {}
    const map = {}
    permisosCentros.centers.forEach(entry => {
      const [region, area] = entry.split('-')
      if (!region || !area) return
      if (!map[region]) map[region] = new Set()
      map[region].add(area)
    })
    // Convertimos Sets en arrays
    Object.keys(map).forEach(region => {
      map[region] = Array.from(map[region])
    })
    return map
  }, [permisosCentros])


  // --- Para permisosJaula: simplemente una lista de nombres de jaulas ---
  const jaulasList = useMemo(() => {
    if (!Array.isArray(permisosJaula)) return []
    return permisosJaula.map(name => ({
      key: getScappCompareKey(name, nivelSeleccionado),
      label: name,
    }))
  }, [permisosJaula, nivelSeleccionado])

  // useEffect(() => {
  // if (safeSelectedCenters.length > 0) return

  //   if (nivelSeleccionado === 'centro') {
  //     // Si el nivel es 'centro', usamos directamente jaulasList
  //     if (jaulasList.length >= 2) {
  //       setSelectedCenters([jaulasList[0], jaulasList[1]])
  //     } else if (jaulasList.length === 1) {
  //       setSelectedCenters([jaulasList[0]])
  //     }
  //   } else if (nivelSeleccionado === 'region') {
  //     // Si el nivel es 'region', usamos los strings "region-area"
  //     if (permisosCentros && Array.isArray(permisosCentros.centers)) {
  //       const areaSet = new Set()
  //       permisosCentros.centers.forEach(entry => {
  //         const [region, area] = entry.split('-')
  //         if (region && area) {
  //           areaSet.add(`${region}-${area}`)
  //         }
  //       })
  //       const areasArray = Array.from(areaSet)
  //       if (areasArray.length >= 2) {
  //         setSelectedCenters([areasArray[0], areasArray[1]])
  //       } else if (areasArray.length === 1) {
  //         setSelectedCenters([areasArray[0]])
  //       }
  //     }
  //   } else {
  //     // Por defecto (caso 'área' → selección de centros)
  //     if (permisosCentros && Array.isArray(permisosCentros.centers)) {
  //       const centrosArray = permisosCentros.centers
  //       if (centrosArray.length >= 2) {
  //         setSelectedCenters([centrosArray[0], centrosArray[1]])
  //       } else if (centrosArray.length === 1) {
  //         setSelectedCenters([centrosArray[0]])
  //       }
  //     }
  //   }
  // }, [nivelSeleccionado])


  // Alterna la selección de un centro/jaula (lo agrega o lo quita)
  // const toggleCenter = key => {
  //   setSelectedCenters(prev =>
  //     prev.includes(key) ? prev.filter(c => c !== key) : [...prev, key]
  //   )
  // }

  const toggleCenter = key => {
    const selectionKey = getScappCompareKey(key, nivelSeleccionado);

    setSelectedCenters(prev => {
      const arr = Array.isArray(prev) ? prev : [];
      const exists = arr.some(item =>
        isSameScappSelection(item, selectionKey, nivelSeleccionado)
      );

      if (exists) {
        return arr.filter(item =>
          !isSameScappSelection(item, selectionKey, nivelSeleccionado)
        );
      }

      return [...arr, selectionKey];
    });
  }


  // const addFromInput = () => {
  //   const val = inputValue.trim()
  //   if (val && !safeSelectedCenters.includes(val)) {
  //     setSelectedCenters(prev => [...prev, val])
  //   }
  //   setInputValue('')
  // }

  const addFromInput = () => {
    const val = inputValue.trim();
    const selectionKey = getScappCompareKey(val, nivelSeleccionado);
    setSelectedCenters(prev => {
      const arr = Array.isArray(prev) ? prev : [];
      const exists = arr.some(item =>
        isSameScappSelection(item, selectionKey, nivelSeleccionado)
      );
      return val && !exists
        ? [...arr, selectionKey]
        : arr;
    });
    setInputValue("");
  }


  const removeCenter = key => {
    setSelectedCenters(prev =>
      (Array.isArray(prev) ? prev : []).filter(item =>
        !isSameScappSelection(item, key, nivelSeleccionado)
      )
    )
  }

  const handleKeyDown = e => {
    if (e.key === 'Enter') addFromInput()
  }

  const handleCompare = async () => {

    // Si nivel seleccionado es 'centro', sólo cerrar drawer
    if (nivelSeleccionado === 'centro') {
      closeDrawer();
      return;
    }

    const { startDate, dayRange } = dias || {}
    if (!startDate || !dayRange) return
    if (safeSelectedCenters.length < 2) return

    try {
      const param = safeSelectedCenters.join(',')
      console.log('param')
      const result = nivelSeleccionado === 'region'
        ? await compare_analytic_areas(param, startDate, dayRange, servidor)
        : await compare_analytic_centers(param, startDate, dayRange, servidor)

      // console.log('Resultado comparación:', result)
      // ✅ Normalizar claves mal escritas (visitas_cout → visitas_count, perdidos_cout → perdidos_count)
      const normalized = normalizeVersusResult(result);
      setVersus(normalized);
      // setVersus(result)
    } catch (err) {
      console.error(err)
    }
  }


  const getCenterName = fullKey => {
    const parts = fullKey?.split('-')
    if (nivelSeleccionado === 'region') {
      return parts?.length >= 2 ? parts[1] : fullKey
    }
    return parts?.length === 3 ? parts[2] : fullKey
  }

  const getSelectedLabel = (value) => {
    if (nivelSeleccionado === 'centro') {
      return findScappSelectionLabel(value, permisosJaula ?? [], nivelSeleccionado);
    }

    return getCenterName(value);
  }


  const sortedAsc = [...jaulasList].sort((a, b) =>
    a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' })
  );


  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={closeDrawer}
      sx={{
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: '14.5vw',
          height: '100vh',
          borderLeft: '0.1vw solid #DE4E11',
          backgroundColor: '#13131f',
          display: 'flex'
        }
      }}
    >
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          p: '2vh',
          boxSizing: 'border-box',
          backgroundColor: '#13131f'
        }}
      >
        {/* Cabecera del Drawer */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: '.5vh' }}>
          <Typography
            variant="h6"
            sx={{
              flexGrow: 1,
              color: '#fff',
              fontSize: '.9vw',
              userSelect: 'none',
              cursor: 'default'
            }}
          >
            {t("versus_short", "V/S")}
          </Typography>
          <IconButton edge="end" onClick={closeDrawer} sx={{ color: '#fff' }}>
            <CloseIcon style={{fontSize:'0.9vw'}} />
          </IconButton>
        </Box>
        <Divider sx={{ borderColor: '#444' }} />

        {/* --- Sección de comparación: ítems 'vs' seleccionados en línea --- */}
        <Box sx={{ mt: '1vw' }}>
          <Typography
            variant="subtitle1"
            sx={{ color: '#fff', fontSize: '0.7vw', userSelect: 'none', cursor: 'default' }}
          >
            {t('items_selected_compare', 'Selección para comparar')}
          </Typography>

          {safeSelectedCenters.length > 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                mt: '0.5vh'
              }}
            >
              {safeSelectedCenters.map((fullKey, i) => {
                const centerOnly = getSelectedLabel(fullKey)
                return (
                  <Box
                    key={`${fullKey}-${i}`}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mr: '0.2vw',
                      mb: '0.5vh'
                    }}
                  >
                    <Typography
                      sx={{
                        color: '#fff',
                        mr: '0.2vw',
                        fontSize: '0.6vw',
                        userSelect: 'none',
                        cursor: 'default'
                      }}
                    >
                      {labelId(fullKey, nivelSeleccionado === 'region' ? 'ra' : 'rac', centerOnly)}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => removeCenter(fullKey)}
                      sx={{ color: '#fff', p: '0.4vw', }}
                    >
                      <CloseIcon fontSize="small" style={{fontSize: '0.6vw',}} />
                    </IconButton>
                    {i < safeSelectedCenters.length - 1 && (
                      <Typography
                        sx={{
                          mx: '0.4vw',
                          color: '#DE4E11',
                          fontWeight: 'bold',
                          fontSize: '0.8vw',
                          userSelect: 'none',
                          cursor: 'default'
                        }}
                      >
                        {t("versus_short", "V/S").toLowerCase()}
                      </Typography>
                    )}
                  </Box>
                )
              })}
            </Box>
          ) : (
            <Typography
              variant="body2"
              sx={{ color: '#c3c3c3', mt: '1vw', fontSize: '0.6vw', userSelect: 'none', cursor: 'default' }}
            >
              {t('no_items_selected', 'No hay ítems seleccionados.')}
            </Typography>
          )}

          {/* Botón de “Comparar Selección” */}
          <Box sx={{ textAlign: 'center', mt: '0.5vh' }}>
            {safeSelectedCenters.length >= 2 ? (
              <Button
                variant="contained"
                size="small"
                onClick={handleCompare}
                sx={{
                  textTransform: 'none',
                  backgroundColor: '#DE4E11',
                  color: '#fff',
                  fontSize: '0.7vw',
                  height:'2.5vh',
                  width:'8vw',
                  '&:hover': { backgroundColor: '#c1440d' }
                }}
              >
                {t('compare_selection', 'Comparar selección')}
              </Button>
            ) : (
              <Typography
                variant="caption"
                sx={{ color: '#DE4E11', fontSize: '0.6vw', userSelect: 'none', cursor: 'default' }}
              >
                {t('select_at_least_two_items', 'Seleccione al menos dos ítems')}
              </Typography>
            )}
          </Box>
        </Box>

        <Divider sx={{ my: '1.5vh', borderColor: '#444' }} />

        {/* --- Listado de ítems disponibles según nivelSeleccionado --- */}
        <Box sx={{ flexGrow: 1, overflowY: 'auto', mt: '1vh' }}>
          {nivelSeleccionado === 'centro' ? (
            jaulasList.length > 0 ? (
              <Box>
                { 
                // jaulasList
                sortedAsc.map(jaula => {
                  const isSelected = selectedKeys.includes(jaula.key);
                  return (
                    <Box
                      key={jaula.key}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: '1vh',
                        px: '1vw',
                        py: '0.8vh',
                        height: '4vh',
                        borderRadius: '0.5vw',
                        '&:hover': {
                          backgroundColor: '#1f1f2a',
                        },
                      }}
                    >
                      <Typography
                        sx={{
                          color: '#fff',
                          fontSize: '0.6vw',
                          userSelect: 'none',
                          cursor: 'default',
                        }}
                      >
                        {jaula.label}
                      </Typography>
                      <Button
                        size="small"
                        onClick={() => toggleCenter(jaula.key)}
                        disabled={!isSelected && isMaxReached}
                        sx={{
                          textTransform: 'none',
                          border: '0.01vw solid #DE4E11',
                          color: isSelected ? '#fff' : '#DE4E11',
                          backgroundColor: isSelected ? '#DE4E11' : 'transparent',
                          '&:hover': {
                            backgroundColor: '#DE4E11',
                            borderColor: isSelected ? '#fff' : '#DE4E11',
                            color: '#fff',
                          },
                          fontSize: '0.7vw',
                          height: '2.5vh',
                        }}
                      >
                        + {t("versus_short", "V/S").toLowerCase()}
                      </Button>
                    </Box>
                  );
                })}
              </Box>
            ) : (
              <Typography
                variant="body2"
                align="center"
                sx={{ color: '#888', fontSize: '1vw', userSelect: 'none', cursor: 'default' }}
              >
                {t("no_cages_available", "No cages available")}
              </Typography>
            )
          ) : nivelSeleccionado === 'area' ? (
            Object.entries(regions).length > 0 ? (
              Object.entries(regions).map(([region, areasObj]) => (
                <Box key={region} sx={{ mb: '.5vh' }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      mb: '0.5vh',
                      fontSize: '0.7vw',
                      color: '#fff',
                      userSelect: 'none',
                      cursor: 'default',
                    }}
                  >
                    {labelRegion(region, region)}
                  </Typography>
                  {Object.entries(areasObj).map(([area, centers]) => (
                    <Box key={area} sx={{ mb: '.5vh', pl: '1vw' }}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          mb: '0.5vh',
                          fontSize: '0.7vw',
                          color: '#bbb',
                          userSelect: 'none',
                          cursor: 'default',
                        }}
                      >
                        {labelArea({ region, area }, area)}
                      </Typography>
                      {centers.map(center => {
                        const fullKey = `${region}-${area}-${center}`;
                        const isSelected = safeSelectedCenters.includes(fullKey);
                        return (
                          <Box
                            key={fullKey}
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              mb: '0.5vh',
                              pl: '1vw',
                              height: '4vh',
                              borderRadius: '0.5vw',
                              '&:hover': {
                                backgroundColor: '#1f1f2a',
                              },
                            }}
                          >
                            <Typography
                              sx={{
                                color: '#fff',
                                fontSize: '0.6vw',
                                userSelect: 'none',
                                cursor: 'default',
                              }}
                            >
                              {labelCentro({ region, area, centro: center }, center)}
                            </Typography>
                            <Button
                              size="small"
                              onClick={() => toggleCenter(fullKey)}
                              disabled={!isSelected && isMaxReached}
                              sx={{
                                textTransform: 'none',
                                border: '0.01vw solid #DE4E11',
                                color: isSelected ? '#fff' : '#DE4E11',
                                backgroundColor: isSelected ? '#DE4E11' : 'transparent',
                                '&:hover': {
                                  backgroundColor: '#DE4E11',
                                  borderColor: isSelected ? '#fff' : '#DE4E11',
                                  color: '#fff',
                                },
                                fontSize: '0.7vw',
                                height: '2.5vh',
                                mr: '1vw',
                              }}
                            >
                              + {t("versus_short", "V/S").toLowerCase()}
                            </Button>
                          </Box>
                        );
                      })}
                    </Box>
                  ))}
                  <Divider sx={{ my: '.5vh', borderColor: '#444' }} />
                </Box>
              ))
            ) : (
              <Typography
                variant="body2"
                align="center"
                sx={{ color: '#888', fontSize: '1vw', userSelect: 'none', cursor: 'default' }}
              >
                {t("no_available_centers", "No centers available")}
              </Typography>
            )
          ) : Object.entries(regionAreaMap).length > 0 ? (
            Object.entries(regionAreaMap).map(([region, areas]) => (
              <Box key={region} sx={{ mb: '.5vh' }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    mb: '0.5vh',
                    fontSize: '0.7vw',
                    color: '#fff',
                    userSelect: 'none',
                    cursor: 'default',
                  }}
                >
                  {labelRegion(region, region)}
                </Typography>
                {areas.map(area => {
                  const fullKey = `${region}-${area}`;
                  const isSelected = safeSelectedCenters.includes(fullKey);
                  return (
                    <Box
                      key={fullKey}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: '0.5vh',
                        pl: '1vw',
                        height: '4vh',
                        borderRadius: '0.5vw',
                        '&:hover': {
                          backgroundColor: '#1f1f2a',
                        },
                      }}
                    >
                      <Typography sx={{ color: '#fff', fontSize: '0.6vw' }}>{labelArea({ region, area }, area)}</Typography>
                      <Button
                        size="small"
                        onClick={() => toggleCenter(fullKey)}
                        sx={{
                          textTransform: 'none',
                          border: '0.01vw solid #DE4E11',
                          color: isSelected ? '#fff' : '#DE4E11',
                          backgroundColor: isSelected ? '#DE4E11' : 'transparent',
                          '&:hover': {
                            backgroundColor: '#DE4E11',
                            borderColor: isSelected ? '#fff' : '#DE4E11',
                            color: '#fff',
                          },
                          fontSize: '0.7vw',
                          height: '2.5vh',
                          mr: '1vw',
                        }}
                      >
                        + {t("versus_short", "V/S").toLowerCase()}
                      </Button>
                    </Box>
                  );
                })}
                <Divider sx={{ my: '.5vh', borderColor: '#444' }} />
              </Box>
            ))
          ) : null}
        </Box>
      </Box>
    </Drawer>
  )
}

export default SelectorData
